import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { errorHandler } from "#app/monitoring/errorHandler";
import { showErrorToast } from "#app/notifications";
import { useCompany } from "#modules/company/view/useCompany";
import {
  bulkUpdateMaterial,
  BulkUpdateMaterialPayload,
} from "#modules/quoteRequestLines/infra/bulkUpdateMaterial.api";
import {
  getQuoteRequestLineQueryData,
  setQuoteRequestLineQueryData,
} from "#modules/quoteRequestLines/infra/quoteRequestLine.query";
import { AnalyticsEvent, useAnalytics } from "#shared/useAnalytics";
import { JobStatusSchema } from "shared";

export const useBulkUpdateMaterial = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const analytics = useAnalytics();
  const { company } = useCompany();

  const { mutate, mutateAsync, ...rest } = useMutation({
    mutationFn: (payload: BulkUpdateMaterialPayload) => bulkUpdateMaterial(payload),
    onMutate: ({ ids }) => {
      ids.forEach((id) => {
        const previousQuoteRequestLine = getQuoteRequestLineQueryData(queryClient, id);

        if (previousQuoteRequestLine?.predictionStatus === JobStatusSchema.enum.PROCESSED) {
          analytics.capture(AnalyticsEvent.PredictionRerun, {
            quote_request_line_id: id,
            trigger: "bulkUpdateMaterial",
            company_id: company?.id ?? null,
            company_name: company?.name ?? null,
          });
        }
      });
    },
    onSuccess: (response) => {
      response.updatedQuoteRequestLines.forEach((quoteRequestLine) => {
        const previousQuoteRequestLine = getQuoteRequestLineQueryData(
          queryClient,
          quoteRequestLine.id,
        );

        if (previousQuoteRequestLine) {
          setQuoteRequestLineQueryData(queryClient, quoteRequestLine.id, {
            ...previousQuoteRequestLine,
            materialName: quoteRequestLine.materialName,
            materialGrade: quoteRequestLine.materialGrade,
            predictionStatus: "STANDBY",
          });
        }
      });

      if (response.skippedQuoteRequestLineIds && response.skippedQuoteRequestLineIds.length > 0) {
        showErrorToast({
          color: "orange",
          title: t("quoteRequestLine.bulkActionSkippedAnalyzing", {
            count: response.skippedQuoteRequestLineIds.length,
          }),
        });
      }
    },
    onError: (err: Error) => {
      showErrorToast({
        title: err.message,
      });
      errorHandler(err);
    },
  });

  return { bulkUpdateMaterial: mutate, bulkUpdateMaterialAsync: mutateAsync, ...rest };
};
